import { zodResolver } from '@hookform/resolvers/zod';
import { createFileRoute, Link, redirect } from '@tanstack/react-router';
import { EyeIcon, EyeOffIcon } from 'lucide-react';
import * as React from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { editorStore } from '@/context/store/store';
import { useLoginMutation } from '@/query/auth';
import '@/styles/route-auth.scss';

import { loginDefaultValues, loginSchema, type LoginFormValues } from './login.schema';

const loginSearchSchema = z.object({
  error: z.enum(['access_denied']).optional(),
  email: z.email().optional(),
});

export const Route = createFileRoute('/login')({
  validateSearch: loginSearchSchema,
  beforeLoad: () => {
    const status = editorStore.getState().auth.status;
    if (status === 'authenticated') {
      throw redirect({ to: '/documents' });
    }
    if (status === 'force-change-password') {
      throw redirect({ to: '/force-change-password' });
    }
  },
  component: LoginComponent,
});

function LoginComponent() {
  const { error: routeError, email: prefilledEmail } = Route.useSearch();
  const [showPassword, setShowPassword] = React.useState(false);
  const mutation = useLoginMutation();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormValues>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      ...loginDefaultValues,
      email: prefilledEmail ?? loginDefaultValues.email,
    },
  });

  const onSubmit = (data: LoginFormValues) => {
    if (mutation.isPending) return;
    mutation.mutate({ email: data.email.toLowerCase(), password: data.password });
  };

  const errorMessage =
    routeError === 'access_denied'
      ? 'This account does not have access to the web application.'
      : mutation.error instanceof Error
        ? mutation.error.message
        : mutation.isError
          ? 'Could not sign you in. Check your credentials and try again.'
          : null;

  return (
    <main className="auth">
      <div className="auth__brand">World Athletics · Book of Rules</div>

      <div className="auth__card">
        <h1 className="auth__heading">Sign in.</h1>
        <p className="auth__subtitle">
          Use your administrator credentials to access the regulatory canon.
        </p>

        <form className="auth__form" onSubmit={handleSubmit(onSubmit)} noValidate>
          <div className={`auth__field${errors.email ? ' auth__field--invalid' : ''}`}>
            <input
              id="auth-email"
              className="auth__field-input auth__field-input--no-toggle"
              type="email"
              placeholder=" "
              autoComplete="email"
              {...register('email')}
            />
            <label className="auth__field-label" htmlFor="auth-email">
              Email
            </label>
            {errors.email && (
              <p className="auth__field-error">{errors.email.message}</p>
            )}
          </div>

          <div className={`auth__field${errors.password ? ' auth__field--invalid' : ''}`}>
            <input
              id="auth-password"
              className="auth__field-input"
              type={showPassword ? 'text' : 'password'}
              placeholder=" "
              autoComplete="current-password"
              {...register('password')}
            />
            <label className="auth__field-label" htmlFor="auth-password">
              Password
            </label>
            <button
              type="button"
              className="auth__field-toggle"
              onClick={() => setShowPassword((s) => !s)}
              aria-label={showPassword ? 'Hide password' : 'Show password'}
              aria-pressed={showPassword}
            >
              {showPassword ? (
                <EyeOffIcon size={18} />
              ) : (
                <EyeIcon size={18} />
              )}
            </button>
            {errors.password && (
              <p className="auth__field-error">{errors.password.message}</p>
            )}
          </div>

          <Link to="/forgot-password" className="auth__forgot-link">
            Forgot password?
          </Link>

          {errorMessage && (
            <div role="alert" className="auth__error">
              {errorMessage}
            </div>
          )}

          <button
            type="submit"
            className="auth__submit"
            disabled={mutation.isPending}
          >
            {mutation.isPending ? (
              <>
                <span className="auth__submit-spinner" aria-hidden="true" />
                Signing in…
              </>
            ) : (
              'Sign in'
            )}
          </button>
        </form>
      </div>

      <div className="auth__footer">© World Athletics</div>
    </main>
  );
}
